import AppProvider from "@/app/providers/AppProvider";
import {
  LazyInteractiveSurveyLayout,
  LazyProtectedRoute,
  preloadMainAppComponents,
} from "@/shared/components/LazyComponents";
import LazyErrorBoundary from "@/shared/components/LazyErrorBoundary";
import LoadingSpinner from "@/shared/components/LoadingSpinner";
import { lazy, Suspense, useEffect } from "react";
import { Route, Routes } from "react-router";
import "./App.css";

// Lazy load the interactive survey components
const LazyInteractiveSurveyCover = lazy(
  () => import("@/pages/InteractiveSurveyCover"),
);
const LazyInteractiveSurveyLogin = lazy(
  () => import("@/pages/InteractiveSurveyLogin"),
);
const LazyInteractiveSurveyComparison = lazy(
  () => import("@/pages/InteractiveSurveyComparison"),
);
const LazyInteractiveSurveyMarketMap = lazy(
  () => import("@/pages/InteractiveSurveyMarketMap"),
);
const LazyInteractiveSurveyMarketMapRendered = lazy(
  () => import("@/pages/InteractiveSurveyMarketMapRendered"),
);

const LazyInteractiveSurveyProperty = lazy(
  () => import("@/pages/InteractiveSurveyProperty"),
);
const LazyInteractiveSurveyTour = lazy(
  () => import("@/pages/InteractiveSurveyTourSchedule"),
);
const LazyInteractiveSurveyLeaseComps = lazy(
  () => import("@/pages/InteractiveSurveyLeaseComps"),
);

// Loading fallback component
const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="large" text="Loading page..." />
  </div>
);

function AppRoutes() {
  // Preload critical components after initial render
  useEffect(() => {
    // Preload main app components after a short delay
    const timer = setTimeout(() => {
      preloadMainAppComponents();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <LazyErrorBoundary>
      <Routes>
        {/* Interactive Survey Routes */}
        <Route
          path="/interactive/:surveyId/login"
          element={
            <Suspense fallback={<RouteLoadingFallback />}>
              <LazyInteractiveSurveyLogin />
            </Suspense>
          }
        />

        {/* Protected Interactive Survey Routes */}
        <Route
          element={
            <Suspense fallback={<RouteLoadingFallback />}>
              <LazyProtectedRoute />
            </Suspense>
          }
        >
          <Route
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <LazyInteractiveSurveyLayout />
              </Suspense>
            }
          >
            <Route
              path="/interactive/:surveyId/cover"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyCover />
                </Suspense>
              }
            />
            <Route
              path="/interactive/:surveyId/comparison"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyComparison />
                </Suspense>
              }
            />
            <Route
              path="/interactive/:surveyId/marketmap"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyMarketMap />
                </Suspense>
              }
            />
            <Route
              path="/interactive/:surveyId/marketmapRendered"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyMarketMapRendered />
                </Suspense>
              }
            />
            <Route
              path="/interactive/:surveyId/property/:propertyId"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyProperty />
                </Suspense>
              }
            />
            <Route
              path="/interactive/:surveyId/tour"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyTour />
                </Suspense>
              }
            />
            <Route
              path="/interactive/:surveyId/leasecomps"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <LazyInteractiveSurveyLeaseComps />
                </Suspense>
              }
            />
          </Route>
        </Route>
      </Routes>
    </LazyErrorBoundary>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppRoutes />
    </AppProvider>
  );
}
